import { SiteUrls } from '@tableau/shared-urls';
import { MessageWithAndWithoutCount } from '../../../data-catalog-shared';
import { LineageItemType } from '../lineage/LineageItemType';

export enum LineageSectionType {
  Group,
  Anchor,
  Owners,
}

export type LineageItemConfig<TCounts, TPaneParams> = {
  readonly lineageItemType: LineageItemType;
  readonly messagesForLineage: MessageWithAndWithoutCount;
  readonly messagesForPane: MessageWithAndWithoutCount;
  readonly count: (counts: TCounts) => number;
  readonly path: (urls: SiteUrls, itemId: string) => string;
  readonly paneFactory: PaneFactory<TPaneParams>;
};

export type PaneFactory<TPaneParams> = (paneParams: TPaneParams) => JSX.Element;

export type LineageSection<TCounts, TPaneParams> = {
  readonly sectionType: LineageSectionType;
  readonly key: string;
  readonly items: ReadonlyArray<LineageItemConfig<TCounts, TPaneParams>>;
};

export function lineageItemKey(params: {
  lineageItem: LineageItemConfig<unknown, unknown>,
  lineageSection: LineageSection<unknown, unknown>,
}): string {
  const sectionKey = params.lineageSection.key;
  const itemKey = params.lineageItem.lineageItemType;
  return `${sectionKey}-${itemKey}`;
}

/**
 * The anchor in the lineage nav uses its own special anchorCount, but in many cases, we want it to
 * be the same as the anchor pane's count. This helper function extracts the anchor count from the
 * configuration so it can be passed into the LineageNav separately but still be defined in one place.
 */
export function getAnchorCountFromConfig<TCounts>(config: ReadonlyArray<LineageSection<TCounts, unknown>>, counts: TCounts): number | null {
  const anchorSection = config.find(s => s.sectionType === LineageSectionType.Anchor);
  const anchorItem = anchorSection && anchorSection.items[0];
  return (anchorItem && counts) ? anchorItem.count(counts) : null;
}
